/**
 * @fileoverview GET handler for the "/data_objects/:type_name/:object_id/object_relationships"
 * endpoint. The backend returns a unified paginated list (`{ hits, results }`) with each row tagged
 * by `direction` so the table can render incoming (parent) and outgoing (subaccount) edges together.
 */
import fetch from "@lib/fetch";

import { reactQueryTableFetcherAdapter } from "../reactQueryFetcherAdapters";
import { customObjectsTypeNameObjectIdObjectRelationships } from "./constants";
import type {
  CustomObjectsTypeNameObjectIdObjectRelationshipsParams,
  CustomObjectsTypeNameObjectIdObjectRelationshipsResult,
  CustomObjectsTypeNameObjectIdObjectRelationshipsResultItem,
  RawCustomObjectsTypeNameObjectIdObjectRelationshipsResponse,
  RawObjectRelationshipRow,
} from "./types";
import {
  isCustomObjectsTypeNameObjectIdObjectRelationshipsResult,
  isRawCustomObjectsTypeNameObjectIdObjectRelationshipsResponse,
} from "./validators";

export const toObjectRelationshipTableRow = (
  row: RawObjectRelationshipRow
): CustomObjectsTypeNameObjectIdObjectRelationshipsResultItem => ({
  // Row id mirrors the edge's full natural key (direction + related type + rel_kind + object id)
  // so it stays unique: object_id is scoped per type, so two different-type objects can share one.
  id: `${row.direction}:${row.type_name ?? ""}:${row.rel_kind ?? ""}:${row.object_id}`,
  object_id: row.object_id,
  display_name: row.display_name ?? null,
  type_name: row.type_name ?? null,
  rel_kind: row.rel_kind ?? null,
  relationship_name: row.relationship_name ?? null,
  direction: row.direction,
});

export const mapObjectRelationshipsResponse = (
  response: RawCustomObjectsTypeNameObjectIdObjectRelationshipsResponse
): CustomObjectsTypeNameObjectIdObjectRelationshipsResult => {
  return {
    hits: response.hits,
    results: response.results.map(toObjectRelationshipTableRow),
  };
};

export const getDataTableCustomObjectsTypeNameObjectIdObjectRelationships = reactQueryTableFetcherAdapter(
  customObjectsTypeNameObjectIdObjectRelationships,
  (
    signal: AbortSignal,
    {
      appGroupId,
      typeName,
      objectId,
      limit = 12,
      start = 0,
      rel_kind,
    }: CustomObjectsTypeNameObjectIdObjectRelationshipsParams
  ): Promise<CustomObjectsTypeNameObjectIdObjectRelationshipsResult> => {
    return fetch<RawCustomObjectsTypeNameObjectIdObjectRelationshipsResponse>(
      `/data_objects/${encodeURIComponent(typeName)}/${encodeURIComponent(objectId)}/object_relationships`,
      "GET",
      {
        app_group_id: appGroupId,
        limit,
        start,
        rel_kind,
      },
      { signal },
      { propagateFetchRequestError: false }
    ).then((response) => {
      if (!isRawCustomObjectsTypeNameObjectIdObjectRelationshipsResponse(response)) {
        throw new Error("Invalid object relationships response");
      }

      return mapObjectRelationshipsResponse(response);
    });
  },
  isCustomObjectsTypeNameObjectIdObjectRelationshipsResult
);
